import type { TankSpecial } from "./tankTypes";
import { TANK_CATALOG } from "./tankCatalog.data";

/**
 * Regras dos poderes passivos de linhagem (specials) — compartilhadas entre
 * cliente e servidor para que solo e multiplayer usem exatamente os mesmos
 * números:
 *  - shield:    reduz todo dano recebido
 *  - stealth:   sem atirar por um tempo, some do radar dos inimigos distantes
 *  - regen:     regenera casco após alguns segundos sem tomar dano
 *  - explosive: impactos causam dano em área ao redor do alvo
 *  - piercing:  projéteis atravessam inimigos (com decaimento de dano)
 *  - homing:    projéteis corrigem a rota rumo ao inimigo mais próximo
 *  - rebirth:   (Fênix do Bestiário) renasce uma vez por vida ao ser destruído
 *  - burn:      (Dragão do Bestiário) o sopro incendeia o alvo (dano contínuo)
 *
 * `power` (tiers 7/8) amplia o efeito: 1 = padrão, 1.25/1.5 = elite.
 * Nos modos locais o cliente aplica as regras; em multiplayer é o servidor
 * Colyseus que as aplica de forma autoritativa, derivando o poder do tankId
 * validado contra o catálogo canônico (nunca do que o cliente declarar).
 */

// ── Shield: redução passiva de dano ──────────────────────────────────────────
const SHIELD_BASE_REDUCTION = 0.2;

export function applyShieldReduction(damage: number, power = 1): number {
  const reduction = Math.min(0.5, SHIELD_BASE_REDUCTION * power);
  return Math.max(1, Math.round(damage * (1 - reduction)));
}

export function getShieldReductionPercent(power = 1): number {
  return Math.round(Math.min(0.5, SHIELD_BASE_REDUCTION * power) * 100);
}

// ── Stealth: some do radar ao ficar sem atirar ───────────────────────────────
/** Tempo sem atirar para entrar em furtividade (ms). */
export const STEALTH_ACTIVATION_MS = 2500;
/** Fora deste raio, inimigos perdem o jogador furtivo. */
const STEALTH_BASE_DETECTION_RADIUS = 12;

export function getStealthDetectionRadius(power = 1): number {
  // Elite fica invisível mais de perto (raio de detecção menor).
  return STEALTH_BASE_DETECTION_RADIUS / power;
}

// ── Regen: regeneração de casco fora de combate ──────────────────────────────
/** Tempo sem tomar dano para começar a regenerar (ms). */
export const REGEN_DELAY_MS = 4000;
const REGEN_BASE_HP_PER_SECOND = 3;

export function getRegenPerSecond(maxHealth: number, power = 1): number {
  // Base fixa + fração do HP máximo: útil para minis e para titãs.
  return (REGEN_BASE_HP_PER_SECOND + maxHealth * 0.01) * power;
}

// ── Explosive: dano em área no impacto ───────────────────────────────────────
const EXPLOSIVE_BASE_RADIUS = 3.4;
/** Fração do dano do projétil aplicada aos inimigos no raio. */
const EXPLOSIVE_SPLASH_FACTOR = 0.5;

export function getExplosiveRadius(power = 1): number {
  return EXPLOSIVE_BASE_RADIUS * (1 + (power - 1) * 0.6);
}

export function getExplosiveSplashDamage(bulletDamage: number, power = 1): number {
  return Math.max(1, Math.round(bulletDamage * EXPLOSIVE_SPLASH_FACTOR * power));
}

// ── Piercing: projéteis atravessam alvos ─────────────────────────────────────
/** Dano restante após cada perfuração. */
export const PIERCE_FALLOFF = 0.7;

export function getPierceCount(power = 1): number {
  // Padrão atravessa 1 alvo extra; versões elite (1.25+), 2.
  return power >= 1.2 ? 2 : 1;
}

// ── Homing: correção de rota rumo ao alvo ────────────────────────────────────
const HOMING_BASE_TURN_RATE = 2.2; // rad/s de capacidade de curva
/** Raio de aquisição de alvo do projétil teleguiado. */
export const HOMING_ACQUIRE_RADIUS = 18;
/** Cone à frente do projétil dentro do qual ele trava o alvo (cosseno). */
export const HOMING_CONE_COS = 0.35; // ~69° para cada lado

export function getHomingTurnRate(power = 1): number {
  return HOMING_BASE_TURN_RATE * power;
}

// ── Rebirth (Fênix do Bestiário): renasce uma vez por vida ───────────────────
/** Invulnerabilidade concedida no instante do renascimento (ms). */
export const REBIRTH_INVULN_MS = 1500;
const REBIRTH_BASE_HEALTH_FRACTION = 0.3;

export function getRebirthHealthFraction(power = 1): number {
  return Math.min(0.6, REBIRTH_BASE_HEALTH_FRACTION * power);
}

/** HP com que a Fênix renasce (nunca menos que 1). */
export function getRebirthHealth(maxHealth: number, power = 1): number {
  return Math.max(1, Math.round(maxHealth * getRebirthHealthFraction(power)));
}

// ── Burn (sopro do Dragão do Bestiário): incêndio no impacto ─────────────────
/** Duração do incêndio após o impacto (ms). Reaplicar renova, não acumula. */
export const BURN_DURATION_MS = 3000;
/** Intervalo entre ticks de dano do incêndio (ms). */
export const BURN_TICK_MS = 750;
/** Fração do dano do projétil convertida em dano total de incêndio. */
const BURN_BASE_DAMAGE_FRACTION = 0.45;

export function getBurnTotalDamage(bulletDamage: number, power = 1): number {
  return Math.max(1, Math.round(bulletDamage * BURN_BASE_DAMAGE_FRACTION * power));
}

/** Dano de cada tick do incêndio (o total é fatiado pelos ticks da duração). */
export function getBurnTickDamage(bulletDamage: number, power = 1): number {
  const ticks = Math.max(1, Math.round(BURN_DURATION_MS / BURN_TICK_MS));
  return Math.max(1, Math.round(getBurnTotalDamage(bulletDamage, power) / ticks));
}

// ── Resolução canônica tankId → poder ────────────────────────────────────────
export interface ResolvedTankSpecial {
  type: TankSpecial;
  power: number;
}

/** Faixa válida de `specialPower` no catálogo (1 = padrão, 1.5 = elite máx). */
export function clampSpecialPower(power: number): number {
  if (!Number.isFinite(power)) return 1;
  return Math.max(1, Math.min(1.5, power));
}

const SPECIAL_BY_TANK_ID = new Map<string, ResolvedTankSpecial>(
  TANK_CATALOG.filter((t) => t.special).map((t) => [
    t.id,
    { type: t.special as TankSpecial, power: clampSpecialPower(t.specialPower ?? 1) },
  ]),
);

/**
 * Poder de linhagem de um tanque pelo seu id canônico, ou null se o tanque
 * não existe no catálogo ou não tem poder (inclui o tanque inicial).
 * É a única fonte que o servidor aceita — o cliente informa apenas o tankId.
 */
export function resolveTankSpecial(tankId: string | undefined | null): ResolvedTankSpecial | null {
  if (!tankId) return null;
  return SPECIAL_BY_TANK_ID.get(tankId) ?? null;
}
